import { UIOrderType } from '@drift/common';
import { BigNum, OrderType, PRICE_PRECISION_EXP } from '@drift-labs/sdk';
import { TradeFormDerivedState } from 'src/@types/types';
import {
	NEW_ORDER_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING,
	OPEN_POSITION_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING,
} from 'src/constants/warnings';
import { PRICE_BUFFERS } from 'src/constants/trade';
import { getTriggerLimitBufferPrice } from 'src/utils/slippage';
import BN from 'bn.js';
import { PositionDirection } from '@drift-labs/sdk';
import { COMMON_UI_UTILS } from '@drift/common';
import NumLib from '../NumLib';
import { getTickSizeDecimalPlaces } from 'src/hooks/useGetTickSizeForMarket';
import {
	getEffectivePriceDecimals,
	getEffectiveTickSizeInDollars,
} from 'src/constants/marketDecimalConfig';

/**
 * The limit is considered the unexpected side of the trigger if it can't fill immediately from the trigger.
 * @param currentOrderDirection
 * @param limitPrice
 * @param triggerPrice
 * @returns
 */

const getImmediatelyExecutingStopMessageProps = (
	oraclePrice: number,
	triggerPrice: number,
	orderType: UIOrderType,
	currentOrderDirection: 'long' | 'short'
) => {
	return {
		shouldShowMessage: true,
		messageTitle: 'This order will start to execute immediately',
		message: `The current oracle price is ${
			oraclePrice > triggerPrice ? 'above' : 'below'
		} the trigger oracle price.`,
		messageLinkDescription: 'Learn more',
		messageLink: 'https://docs.drift.trade/trading/all-order-types',
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `${orderType}_${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediateActionableStopLimitMessageProps = (
	marketPrice: number,
	limitPrice: number,
	currentOrderDirection: 'long' | 'short'
) => {
	const isLongOrder = currentOrderDirection === 'long';

	// triggered but not executed
	let messageTitle = 'This will place a limit order immediately';
	let message = `The current oracle price is ${
		isLongOrder ? 'below' : 'above'
	} the trigger oracle price.`;

	// triggered and executed
	if (
		(marketPrice <= limitPrice && currentOrderDirection === 'long') ||
		(marketPrice >= limitPrice && currentOrderDirection === 'short')
	) {
		messageTitle = 'This order will start to execute immediately';
		message = `The current market price is ${
			marketPrice > limitPrice ? 'above' : 'below'
		} the limit price.`;
	}

	return {
		shouldShowMessage: true,
		messageTitle,
		message,
		messageLinkDescription: 'Learn more',
		messageLink: 'https://docs.drift.trade/trading/all-order-types',
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `stopLimit_${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediatelyExecutingTakeProfitMessageProps = (
	oraclePrice: number,
	triggerPrice: number,
	orderType: OrderType,
	currentOrderDirection: 'long' | 'short'
) => {
	return {
		shouldShowMessage: true,
		messageTitle: 'This order will start to execute immediately',
		message: `The current oracle price is ${
			oraclePrice > triggerPrice ? 'above' : 'below'
		} the trigger oracle price.`,
		messageLinkDescription: 'Learn more',
		messageLink: 'https://docs.drift.trade/trading/all-order-types',
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
	};
};

const getImmediateActionableTakeProfitLimitMessageProps = (
	marketPrice: number,
	limitPrice: number,
	currentOrderDirection: 'long' | 'short'
) => {
	const isLongOrder = currentOrderDirection === 'long';

	// triggered but not executed
	let messageTitle = 'This will place a limit order immediately';
	let message = `The current oracle price is ${
		isLongOrder ? 'above' : 'below'
	} the trigger oracle price.`;

	// triggered and executed
	if (
		(marketPrice >= limitPrice && currentOrderDirection === 'long') ||
		(marketPrice <= limitPrice && currentOrderDirection === 'short')
	) {
		messageTitle = 'This order will start to execute immediately';
		message = `The current market price is ${
			marketPrice > limitPrice ? 'above' : 'below'
		} the limit price.`;
	}

	return {
		shouldShowMessage: true,
		messageTitle,
		message,
		messageLinkDescription: 'Learn more',
		messageLink: 'https://docs.drift.trade/trading/all-order-types',
		messageType: 'warning' as const,
		skipMessageInConfirmationModal: false,
		disableTradeButton: false,
		messageId: `takeProfitLimit_${currentOrderDirection}_immediatewarning`,
	};
};

/**
 * Check if the current market state already satisfies triggering the stop loss
 *
 * See docs
 * https://docs.drift.trade/order-types#D9KN6
 *
 * @param orderIsLong
 * @param oraclePrice
 * @param triggerPrice
 * @returns
 */
const isImmediateTriggeringStop = (
	orderIsLong: boolean,
	oraclePrice: number,
	triggerPrice: number
) => {
	if (orderIsLong && oraclePrice > triggerPrice) {
		// If order is STOP & LONG, then the TRIGGER CONDITION is ABOVE. So if ORACLE PRICE > TRIGGER PRICE, the order will fill immediately
		return true;
	} else if (!orderIsLong && oraclePrice < triggerPrice) {
		// If order is STOP & SHORT, then the TRIGGER CONDITION is BELOW. So if ORACLE PRICE < TRIGGER PRICE, the order will fill immediately
		return true;
	}

	return false;
};

/**
 * Check if the current market state already satisfied triggering the take profit
 *
 * See docs
 * https://docs.drift.trade/order-types#Bt7aa
 *
 * @param orderIsLong
 * @param oraclePrice
 * @param triggerPrice
 * @returns
 */
const isImmediateTriggeringTakeProfit = (
	orderIsLong: boolean,
	oraclePrice: number,
	triggerPrice: number
) => {
	if (orderIsLong && oraclePrice < triggerPrice) {
		// If order is TAKE PROFIT & LONG, the the TRIGGER CONDITION is BELOW. So if ORACLE PRICE < TRIGGER PRICE, then the order will fill immediately
		return true;
	} else if (!orderIsLong && oraclePrice > triggerPrice) {
		// If order is TAKE PROFIT & SHORT, the the TRIGGER CONDITION is ABOVE. So if ORACLE PRICE > TRIGGER PRICE, then the order will fill immediately
		return true;
	}

	return false;
};

// TODO : message and messageTitle are "required" , but aren't both being used in the TpSlOrderFormPopup ?? Makes no sense
export interface MessageInfo extends Pick<
	TradeFormDerivedState,
	| 'messageType'
	| 'shouldShowMessage'
	| 'message'
	| 'skipMessageInConfirmationModal'
	| 'messageLink'
	| 'messageLinkDescription'
	| 'messageTitle'
	| 'messageId'
	| 'messagePosition'
> {
	disableTradeButton?: boolean;
}

const MARK_PRICE_WARNING_BPS_THRESHOLD = 0.0001;

/**
 * Checks for order violations
 * @param orderType
 * @param isSpot
 * @param priceBoxValue
 * @param orderDirection
 * @param marketPrice
 * @param postOnly
 * @param userIsShort
 * @param baseSize
 * @param secondaryPriceboxValue
 * @param currentPositionDirection
 * @returns
 */
export const getMessageInfoForOrderViolation = (
	orderType: UIOrderType,
	priceBoxValue: number,
	marketPrice: number,
	postOnly: boolean,
	secondaryPriceboxValue: number,
	currentOrderDirection: 'long' | 'short',
	oraclePrice: number,
	marketSymbol: string,
	baseSize?: number,
	stepSize?: number,
	source?: 'tradeForm' | 'closePosition' | 'editOrder'
): MessageInfo | undefined => {
	const orderIsLong = currentOrderDirection === 'long';

	if (
		marketSymbol &&
		baseSize &&
		stepSize &&
		baseSize > 0 &&
		stepSize > 0 &&
		baseSize < stepSize &&
		source !== 'tradeForm' // tradeform has its own size input validation
	) {
		return {
			message: `Order size must be greater than ${stepSize} ${marketSymbol}`,
			shouldShowMessage: true,
			messageTitle: `Order size must be greater than ${stepSize} ${marketSymbol}`,
			messageType: 'error',
			skipMessageInConfirmationModal: false,
			messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
			disableTradeButton: true,
		};
	}

	// A zero/empty price is not a valid basis for order violation warnings
	if (!priceBoxValue) {
		return undefined;
	}

	switch (orderType) {
		case 'limit': {
			const limitPrice = priceBoxValue;

			const messageTitle = postOnly
				? 'This post only order may fail'
				: 'This order will start to execute immediately';

			const isPastMarkPriceThreshold = priceAboveMarkPriceThreshold(
				marketPrice,
				limitPrice,
				orderIsLong
			);

			if (orderIsLong) {
				if (isPastMarkPriceThreshold) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is above or equal to the current orderbook ask price`,
						messageType: 'warning',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					};
				}
			} else {
				if (isPastMarkPriceThreshold) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is below the current ask price`,
						messageType: 'warning',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					};
				}
			}
			break;
		}
		case 'stopMarket': {
			const triggerPrice = priceBoxValue;

			if (isImmediateTriggeringStop(orderIsLong, marketPrice, triggerPrice)) {
				return getImmediatelyExecutingStopMessageProps(
					oraclePrice,
					triggerPrice,
					orderType,
					currentOrderDirection
				);
			}
			break;
		}
		case 'stopLimit': {
			const triggerPrice = priceBoxValue;
			const limitPrice = secondaryPriceboxValue;

			if (isImmediateTriggeringStop(orderIsLong, oraclePrice, triggerPrice)) {
				return getImmediateActionableStopLimitMessageProps(
					marketPrice,
					limitPrice,
					currentOrderDirection
				);
			}

			break;
		}
		case 'takeProfitMarket': {
			const triggerPrice = priceBoxValue;

			if (
				isImmediateTriggeringTakeProfit(orderIsLong, oraclePrice, triggerPrice)
			) {
				return getImmediatelyExecutingTakeProfitMessageProps(
					oraclePrice,
					triggerPrice,
					orderType,
					currentOrderDirection
				);
			}
			break;
		}
		case 'takeProfitLimit': {
			const triggerPrice = priceBoxValue;
			const limitPrice = secondaryPriceboxValue;

			if (
				isImmediateTriggeringTakeProfit(orderIsLong, oraclePrice, triggerPrice)
			) {
				return getImmediateActionableTakeProfitLimitMessageProps(
					marketPrice,
					limitPrice,
					currentOrderDirection
				);
			}

			break;
		}
		case 'oracleLimit': {
			const offset = priceBoxValue;
			const limitPrice = oraclePrice + offset;

			const messageTitle = postOnly
				? 'This post only order may fail'
				: 'This order will start to execute immediately';

			const isPastMarkPriceThreshold = priceAboveMarkPriceThreshold(
				marketPrice,
				limitPrice,
				orderIsLong
			);
			if (orderIsLong) {
				if (isPastMarkPriceThreshold) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is above or equal to the current orderbook ask price`,
						messageType: postOnly ? 'warning' : 'info',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					};
				}
			} else {
				if (isPastMarkPriceThreshold) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is below or equal to the current bid price`,
						messageType: postOnly ? 'warning' : 'info',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
					};
				}
			}
			break;
		}
		case 'scaledOrders': {
			const startPrice = priceBoxValue;
			const endPrice = secondaryPriceboxValue;

			const messageTitle = postOnly
				? 'Some post only orders will fail'
				: 'Some orders will start to execute immediately';

			if (orderIsLong) {
				const limitPrice = Math.max(startPrice, endPrice);
				if (
					limitPrice >=
					marketPrice * (1 + MARK_PRICE_WARNING_BPS_THRESHOLD)
				) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is above or equal to the current ask price`,
						messageType: 'warning',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
						disableTradeButton: postOnly,
					};
				}
			} else {
				const limitPrice = Math.min(startPrice, endPrice);
				if (
					limitPrice <=
					marketPrice * (1 - MARK_PRICE_WARNING_BPS_THRESHOLD)
				) {
					return {
						messageTitle,
						shouldShowMessage: true,
						message: `The limit price is below the current ask price`,
						messageType: 'warning',
						skipMessageInConfirmationModal: false,
						messageId: `${orderType}${currentOrderDirection}_immediatewarning`,
						disableTradeButton: postOnly,
					};
				}
			}
			break;
		}
		default:
			break;
	}
};

export const getPriceInputLabel = (orderType: UIOrderType) => {
	switch (orderType) {
		case 'market':
			return '';
		case 'limit':
			return 'Price';
		case 'stopLimit':
		case 'stopMarket':
			return 'Stop Price';
		case 'takeProfitMarket':
		case 'takeProfitLimit':
			return 'Trigger Price';
		case 'oracle':
			return 'Oracle Market';
		case 'oracleLimit':
			return 'Price Offset';
		case 'scaledOrders':
			return 'Start Price';
		default:
			console.warn(`getPriceInputLabel: Unhandled order type: ${orderType}`);
			return '';
	}
};

export function orderTypeSwitch<T = UIOrderType>(
	orderType: T,
	cases: {
		//@ts-ignore
		[key in T]: any;
	}
) {
	return cases[orderType];
}

export const getShouldShowSecondaryPriceBox = (orderType: UIOrderType) => {
	switch (orderType) {
		case 'market':
		case 'stopMarket':
		case 'takeProfitMarket':
		case 'oracleLimit':
		case 'oracle':
		case 'limit':
			return false;
		default:
			return true;
	}
};

/**
 * Checks if a limit price is too close to a trigger price for a trigger order.
 * Prices should be passed in at trade precision
 * Returns a warning message if the prices are too close, null otherwise.
 */
export const getLimitPriceVsTriggerPriceWarning = (
	triggerPrice: BN | undefined,
	limitPrice: BN | undefined,
	direction: 'long' | 'short',
	isExistingOrder: boolean
): string | null => {
	if (!triggerPrice || !limitPrice) {
		return null;
	}

	const bufferLimitPrice = getTriggerLimitBufferPrice(
		BigNum.from(triggerPrice, PRICE_PRECISION_EXP),
		direction === 'long' ? PositionDirection.LONG : PositionDirection.SHORT,
		PRICE_BUFFERS.TRIGGER_WARNING_PCT // we use a 0.99% buffer instead of a 1% buffer, because we use 1% for deriving the default limit price from the trigger price. This avoid unnecessary warning caused by the default 1% buffer
	);

	if (bufferLimitPrice.eqZero()) {
		return null;
	}

	const isPastBuffer =
		direction === 'long'
			? limitPrice.lt(bufferLimitPrice.val) && limitPrice.gt(triggerPrice)
			: limitPrice.gt(bufferLimitPrice.val) && limitPrice.lt(triggerPrice);

	if (isPastBuffer) {
		if (isExistingOrder) {
			return OPEN_POSITION_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING;
		}
		return NEW_ORDER_TRIGGER_TOO_CLOSE_TO_LIMIT_WARNING;
	}

	return null;
};

/**
 * Updates price boxes with appropriate buffers based on order type and direction.
 * Extracted from useTradeFormStateEngine for reuse across components.
 */
export const updatePriceBoxesWithBuffers = (
	defaultPrice: BigNum | undefined,
	currentOrderType: UIOrderType,
	prevOrderType: UIOrderType | undefined,
	direction: 'long' | 'short',
	priceBoxValue: string,
	secondaryPriceBoxValue: string,
	primaryPriceBoxUpdate: (value: string) => void,
	secondaryPriceBoxUpdate: (value: string) => void,
	tickSize?: BigNum,
	marketSymbol?: string,
	prevMarketSymbol?: string
) => {
	if (!defaultPrice) return;

	// When switching to order types besides oracle limit & scaled, we skip
	// updating prices if they've already been set. Main exceptions include:
	//   - We're switching FROM oracle limit, in which case we DO update the
	// price box so that the offset price value, which doesn't make sense for
	// other order types, doesn't remain.
	//   - We're switching markets, as the prev market prices are irrelevant.
	const switchedMarket = prevMarketSymbol && prevMarketSymbol !== marketSymbol;
	const shouldUpdatePriceBox =
		!priceBoxValue ||
		(prevOrderType === 'oracleLimit' && currentOrderType !== 'oracleLimit') ||
		switchedMarket;
	const shouldUpdateSecondaryPriceBox =
		!secondaryPriceBoxValue || switchedMarket;

	switch (currentOrderType) {
		case 'limit': {
			if (!shouldUpdatePriceBox) {
				break;
			}

			// Add 1% buffer to default price to avoid fluctuating oracle price
			// Buy orders: price - 1% (lower than mark price)
			// Sell orders: price + 1% (higher than mark price)
			const limitPriceToUse = defaultPrice;

			const limitPriceWithBuffer =
				direction === 'long'
					? limitPriceToUse.toNum() * (1 - PRICE_BUFFERS.DEFAULT_FRACTION) // Buy: reduce price by 1_FRACTION%
					: limitPriceToUse.toNum() * (1 + PRICE_BUFFERS.DEFAULT_FRACTION); // Sell: increase price by 1_FRACTION%

			const tickSizeDecimalPlace = tickSize
				? getTickSizeDecimalPlaces(tickSize.val)
				: 0;

			const effectiveDecimalPlaces = marketSymbol
				? getEffectivePriceDecimals(marketSymbol, tickSizeDecimalPlace)
				: tickSizeDecimalPlace;

			primaryPriceBoxUpdate(
				COMMON_UI_UTILS.trimTrailingZeros(
					limitPriceWithBuffer.toFixed(effectiveDecimalPlaces),
					0
				)
			);
			break;
		}
		case 'oracleLimit': {
			if (!tickSize || !marketSymbol) {
				primaryPriceBoxUpdate(
					tickSize
						? direction === 'short'
							? tickSize.prettyPrint()
							: tickSize.neg().prettyPrint()
						: ''
				);
			} else {
				// Calculate effective tick size in dollars based on market configuration
				const tickSizeInDollars = tickSize.toNum();
				const effectiveTickSizeInDollars = getEffectiveTickSizeInDollars(
					marketSymbol,
					tickSizeInDollars
				);

				const effectiveTickSizeValue =
					direction === 'short'
						? effectiveTickSizeInDollars
						: -effectiveTickSizeInDollars;

				const tickSizeDecimalPlace = getTickSizeDecimalPlaces(tickSize.val);
				const effectiveDecimalPlaces = getEffectivePriceDecimals(
					marketSymbol,
					tickSizeDecimalPlace
				);

				primaryPriceBoxUpdate(
					COMMON_UI_UTILS.trimTrailingZeros(
						effectiveTickSizeValue.toFixed(effectiveDecimalPlaces),
						0
					)
				);
			}
			break;
		}
		case 'stopMarket':
		case 'takeProfitMarket':
		case 'stopLimit':
		case 'takeProfitLimit': {
			const useHigherPrice =
				(direction === 'short' &&
					(currentOrderType == 'takeProfitMarket' ||
						currentOrderType == 'takeProfitLimit')) ||
				(direction === 'long' &&
					(currentOrderType == 'stopMarket' ||
						currentOrderType == 'stopLimit'));

			const isAdvancedLimit =
				currentOrderType == 'stopLimit' ||
				currentOrderType == 'takeProfitLimit';

			let primaryPriceBoxVal = Number(defaultPrice.toTradePrecision());
			let secondaryPriceBoxVal = Number(defaultPrice.toTradePrecision());

			// Apply 1% buffer to trigger orders to avoid immediate execution
			const isTriggerOrder =
				currentOrderType == 'stopMarket' ||
				currentOrderType == 'stopLimit' ||
				currentOrderType == 'takeProfitMarket' ||
				currentOrderType == 'takeProfitLimit';

			if (isTriggerOrder) {
				if (useHigherPrice) {
					primaryPriceBoxVal =
						primaryPriceBoxVal * (1 + PRICE_BUFFERS.DEFAULT_FRACTION);
				} else {
					primaryPriceBoxVal =
						primaryPriceBoxVal * (1 - PRICE_BUFFERS.DEFAULT_FRACTION);
				}
			}

			if (
				currentOrderType === 'stopLimit' ||
				currentOrderType === 'takeProfitLimit'
			) {
				secondaryPriceBoxVal = getTriggerLimitBufferPrice(
					BigNum.fromPrint(primaryPriceBoxVal.toString(), PRICE_PRECISION_EXP),
					direction === 'long'
						? PositionDirection.LONG
						: PositionDirection.SHORT
				).toNum();
			}

			if (shouldUpdatePriceBox) {
				primaryPriceBoxUpdate(
					primaryPriceBoxVal
						? NumLib.formatNum.toTradePrecision(primaryPriceBoxVal).toString()
						: ''
				);
			}

			if (isAdvancedLimit && shouldUpdateSecondaryPriceBox) {
				secondaryPriceBoxUpdate(
					secondaryPriceBoxVal
						? NumLib.formatNum.toTradePrecision(secondaryPriceBoxVal).toString()
						: ''
				);
			}

			break;
		}

		case 'scaledOrders': {
			let primaryPriceBoxVal = Number(defaultPrice.toTradePrecision());
			let secondaryPriceBoxVal = Number(defaultPrice.toTradePrecision());

			if (direction === 'short') {
				primaryPriceBoxVal =
					primaryPriceBoxVal * (1 + PRICE_BUFFERS.DEFAULT_FRACTION);
				secondaryPriceBoxVal =
					secondaryPriceBoxVal * (1 + PRICE_BUFFERS.SCALED_ORDERS_FRACTION);
			} else {
				primaryPriceBoxVal =
					primaryPriceBoxVal * (1 - PRICE_BUFFERS.DEFAULT_FRACTION);
				secondaryPriceBoxVal =
					secondaryPriceBoxVal * (1 - PRICE_BUFFERS.SCALED_ORDERS_FRACTION);
			}

			primaryPriceBoxUpdate(
				primaryPriceBoxVal
					? NumLib.formatNum.toTradePrecision(primaryPriceBoxVal).toString()
					: ''
			);
			secondaryPriceBoxUpdate(
				secondaryPriceBoxVal
					? NumLib.formatNum.toTradePrecision(secondaryPriceBoxVal).toString()
					: ''
			);

			break;
		}

		case 'market':
		case 'oracle':
			if (shouldUpdatePriceBox) {
				primaryPriceBoxUpdate(defaultPrice.toNum().toString());
			}

			break;
		default: {
			const exhaustiveCheck: never = currentOrderType;
			console.warn(
				`updatePriceBoxesWithBuffers: Unhandled order type: ${exhaustiveCheck}`
			);
			break;
		}
	}
};

const priceAboveMarkPriceThreshold = (
	markPrice: number,
	limitPrice: number,
	isLong: boolean
) => {
	if (isLong) {
		return limitPrice >= markPrice * (1 + MARK_PRICE_WARNING_BPS_THRESHOLD);
	}
	return limitPrice <= markPrice * (1 - MARK_PRICE_WARNING_BPS_THRESHOLD);
};
